import { getGenerativeModel } from 'firebase/ai';
import { ai } from '../../../../config/firebaseConfig.js';
import { DEFAULT_AI_GENERATION_CONFIG } from '../../config/aiModelConfig.js';
import modelPriorityConfig from '../config/aiModelPriority.json';

const configuredModelNames = Array.isArray(modelPriorityConfig.models)
  ? [...new Set(modelPriorityConfig.models.map(String).map((name) => name.trim()).filter(Boolean))]
  : [];
if (!configuredModelNames.length) {
  throw new Error('NewBlogSystem AI model priority must contain at least one model.');
}

export const HTML_EDITOR_AI_MODEL_NAMES = Object.freeze(configuredModelNames);
export const HTML_EDITOR_AI_MODEL_NAME = HTML_EDITOR_AI_MODEL_NAMES[0];
export const HTML_EDITOR_FALLBACK_AI_MODEL_NAME = HTML_EDITOR_AI_MODEL_NAMES[1]
  || HTML_EDITOR_AI_MODEL_NAME;
export const HTML_EDITOR_MAX_OUTPUT_TOKENS = 32768;
export const HTML_EDITOR_EDIT_MAX_OUTPUT_TOKENS = 16384;
export const HTML_EDITOR_FOCUSED_MAX_OUTPUT_TOKENS = 16384;
export const HTML_EDITOR_PARTIAL_MAX_OUTPUT_TOKENS = 8192;

const BASE64_DATA_URI_PATTERN = /data:[^;,\s]{1,100};base64,[a-z0-9+/=\r\n]+/gi;
const BINARY_PROMPT_KEYS = new Set(['base64', 'datauri', 'imagedata', 'inlinedata']);
// Prompt data is treated as untrusted until binary fields and data URIs are removed.
export const redactBase64FromAiPrompt = (value) => {
  if (typeof value === 'string') {
    return value.replace(BASE64_DATA_URI_PATTERN, '[image-data-omitted]');
  }
  if (Array.isArray(value)) {
    return value.map(redactBase64FromAiPrompt);
  }
  if (!value || typeof value !== 'object') return value;

  return Object.fromEntries(Object.entries(value).map(([key, entry]) => [
    key,
    BINARY_PROMPT_KEYS.has(key.toLowerCase())
      ? '[image-data-omitted]'
      : redactBase64FromAiPrompt(entry),
  ]));
};

const requireAiClient = () => {
  if (!ai) {
    throw new Error('The AI assistant is not available right now. Please try again later.');
  }
  return ai;
};

const createJsonModel = ({
  generationConfig = {},
  modelName = HTML_EDITOR_AI_MODEL_NAME,
} = {}) => getGenerativeModel(requireAiClient(), {
  model: modelName,
  generationConfig: {
    maxOutputTokens: DEFAULT_AI_GENERATION_CONFIG.maxOutputTokens,
    ...generationConfig,
    responseMimeType: 'application/json',
  },
});

const parseJsonResponse = (rawText = '') => {
  const trimmed = String(rawText || '').trim();
  if (!trimmed) {
    throw new Error('The AI assistant did not return a result. Please try again.');
  }

  try {
    return JSON.parse(trimmed);
  } catch (_) {
    return JSON.parse(
      trimmed
        .replace(/^```json\s*/i, '')
        .replace(/^```\s*/i, '')
        .replace(/\s*```$/, '')
    );
  }
};

const maxTokensError = () => {
  const error = new Error('The AI assistant reached its response limit.');
  error.code = 'AI_MAX_TOKENS';
  return error;
};

// This is the only NewBlogSystem boundary that sends text to Firebase AI.
export const requestEditorJson = async ({
  prompt,
  maxOutputTokens = HTML_EDITOR_PARTIAL_MAX_OUTPUT_TOKENS,
  generationConfig = {},
  modelName = HTML_EDITOR_AI_MODEL_NAME,
} = {}) => {
  const model = createJsonModel({
    generationConfig: { ...generationConfig, maxOutputTokens },
    modelName,
  });
  const safePrompt = redactBase64FromAiPrompt(String(prompt || ''));
  const result = await model.generateContent(safePrompt);
  const response = result?.response;
  const finishReason = String(response?.candidates?.[0]?.finishReason || '').toUpperCase();

  if (finishReason === 'MAX_TOKENS') throw maxTokensError();

  let responseText = '';
  try {
    responseText = response?.text?.() || '';
  } catch (responseError) {
    if (/max[_\s-]?tokens|token limit/i.test(String(responseError?.message || ''))) {
      throw maxTokensError();
    }
    const error = new Error(
      'The AI assistant could not complete that request. Try a shorter or more specific description.'
    );
    error.code = 'AI_INCOMPLETE_RESPONSE';
    throw error;
  }

  try {
    return parseJsonResponse(responseText);
  } catch (_) {
    const error = new Error(
      'The AI assistant returned an incomplete result. Try a shorter or more specific description.'
    );
    error.code = 'AI_INCOMPLETE_JSON';
    throw error;
  }
};

// Models are attempted in configured priority order. Later attempts use the compact
// retry prompt and fallback generation settings.
export const requestEditorJsonWithRetry = async ({
  buildPrompt,
  maxOutputTokens,
  retryMaxOutputTokens = maxOutputTokens,
  generationConfig = {},
  fallbackGenerationConfig = generationConfig,
  modelNames = HTML_EDITOR_AI_MODEL_NAMES,
  onRetry,
} = {}) => {
  const prioritizedModels = Array.isArray(modelNames)
    ? [...new Set(modelNames.map(String).map((name) => name.trim()).filter(Boolean))]
    : [];
  if (!prioritizedModels.length) {
    throw new Error('No AI models are configured for this request.');
  }

  let lastError;
  for (let index = 0; index < prioritizedModels.length; index += 1) {
    const retry = index > 0;
    try {
      return await requestEditorJson({
        prompt: buildPrompt(retry),
        maxOutputTokens: retry ? retryMaxOutputTokens : maxOutputTokens,
        generationConfig: retry ? fallbackGenerationConfig : generationConfig,
        modelName: prioritizedModels[index],
      });
    } catch (error) {
      lastError = error;
      const nextModel = prioritizedModels[index + 1];
      if (!nextModel) break;
      onRetry?.(error, {
        failedModel: prioritizedModels[index],
        nextModel,
        attempt: index + 1,
      });
    }
  }

  throw lastError;
};
